import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChartBar, FaLaptopCode, FaShieldAlt, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import './SolutionsCarousel.css';

/* ─── Data ─────────────────────────────────── */
const COUNT = 5; // items per category

const CATEGORIES = [
  {
    id: 'tally',
    label: 'Tally ERP',
    icon: <FaChartBar />,
    items: [
      { label: 'TallyPrime', img: '/images/Primelogo.webp', desc: 'Full ERP accounting & compliance suite', route: '/tallyprime' },
      { label: 'Tally Server', img: '/images/Tallyprime-server.png', desc: 'Multi-user networked server edition', route: '/TallyAws' },
      { label: 'Tally on Cloud', img: '/images/tally_cloud.png', desc: 'Access your books anywhere, anytime', route: '/TallyAws' },
      { label: 'Custom TDL', img: '/images/product-updates (1).png', desc: 'Tally TDL customization & add-ons', route: '/TallyDefinition' },
      { label: 'TSS', img: '/images/tss-logo.png', desc: 'Annual software subscription & support', route: '/TallySoftware' },
      { label: 'TallyPrime Ira', img: '/images/tally-ira/tally-ira-logo.png', desc: 'AI-powered document scanning & invoice uploader', route: '/TallyIra' },
    ],
  },
  {
    id: 'software',
    label: 'Software & Web',
    icon: <FaLaptopCode />,
    items: [
      { label: 'Websites', img: '/images/webapp.png', desc: 'Custom web portal development', route: '/WebDevelopment' },
      { label: 'Mobile Apps', img: '/images/mobile-app.webp', desc: 'Android & iOS applications', route: '/MobileDeveloper' },
      { label: 'Biz Analyst', img: '/images/logotally/unnamed.png', desc: 'Business intelligence & reports', route: '/BizAnalyst' },
      { label: 'E-Commerce', img: '/images/4861083.jpg', desc: 'Online store & payment integration', route: '/WebDevelopment' },
      { label: 'Custom ERP', img: '/images/1232.png', desc: 'Tailored enterprise resource planning', route: '/WebDevelopment' },
    ],
  },
  {
    id: 'hardware',
    label: 'Hardware & Security',
    icon: <FaShieldAlt />,
    items: [
      { label: 'Biometrics', img: '/images/GeotimeMar18_2_finger_web_4ff55f3b-97eb-4f7a-b4a8-a98c7f136383_1350x-removebg-preview (1).png', desc: 'Fingerprint & face attendance systems', route: '/BIO' },
      { label: 'CCTV Systems', img: '/images/shopping.webp', desc: 'Surveillance & monitoring solutions', route: '/SecuritySystem' },
      { label: 'Servers', img: '/images/server.jpg', desc: 'High-performance enterprise servers', route: '/ServerPage' },
      { label: 'Hardwares', img: '/images/71QtoB1MJIL-removebg-preview.png', desc: 'Hardware Systems & peripherals', route: '/HardwareServices' },
      { label: 'Printers', img: '/images/printer.webp', desc: 'Professional printing & scanning solutions', route: '/PrinterSoftware' },
    ],
  },
];

/* ─── Infinite Loop Logic ─────────────────────
   Extended array: [copy1 | copy2 | copy3]
   rawIdx runs:   COUNT → COUNT*2-1, then silently jumps back to COUNT
   This means the user always sees cards from copy2, with copy1 and copy3
   providing seamless overflow on both sides.
─────────────────────────────────────────────── */

/* ─── Category Row ─────────────────────────── */
const CategoryRow = ({ category }) => {
  const navigate = useNavigate();
  const count = category.items.length;
  const [rawIdx, setRawIdx] = useState(count);
  const isResetting = useRef(false);
  const trackRef = useRef(null);
  const autoTimerRef = useRef(null);

  const extended = [...category.items, ...category.items, ...category.items];

  // Logic to start/restart the timer
  const startTimer = () => {
    if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    autoTimerRef.current = setInterval(() => {
      handleNext();
    }, 4000 + Math.random() * 1000); // Slightly slower base speed
  };

  const handleNext = () => {
    setRawIdx(prev => {
      const next = prev + 1;
      if (next >= count * 2) {
        isResetting.current = true;
        return count;
      }
      return next;
    });
  };

  const handlePrev = () => {
    setRawIdx(prev => {
      const next = prev - 1;
      if (next < count) {
        isResetting.current = true;
        return count * 2 - 1;
      }
      return next;
    });
  };

  // Manual trigger with pause
  const handleManualNav = (dir) => {
    if (dir === 'next') handleNext();
    else handlePrev();

    // Clear existing timer and restart after a longer delay (pause)
    if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    setTimeout(startTimer, 6000); // Wait 6 seconds before resuming auto-play
  };

  useEffect(() => {
    startTimer();
    return () => {
      if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isResetting.current) {
      if (trackRef.current) trackRef.current.style.transition = 'none';
      isResetting.current = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (trackRef.current) trackRef.current.style.transition = '';
        });
      });
    }
  }, [rawIdx]);

  return (
    <div className="sc-category-group">
      <div className="sc-row-header">
        <div className="sc-row-info">
          <span className="sc-row-icon">{category.icon}</span>
          <span className="sc-row-label">{category.label}</span>
        </div>

        {/* ── Row Navigation ── */}
        <div className="sc-row-nav">
          <button className="sc-nav-btn-sm" onClick={() => handleManualNav('prev')}><FaArrowLeft /></button>
          <button className="sc-nav-btn-sm" onClick={() => handleManualNav('next')}><FaArrowRight /></button>
        </div>
      </div>

      <div className="sc-row">
        <div className="sc-slider-viewport">
          <div
            ref={trackRef}
            className="sc-slider-track"
            style={{
              transform: `translateX(calc(-1 * ((${rawIdx} + 0.5) * var(--card-step) - var(--card-gap) / 2)))`
            }}
          >
            {extended.map((item, i) => (
              <div
                key={i}
                className={`sc-slide-card ${i === rawIdx ? 'sc-slide-card--active' : ''}`}
                onClick={() => navigate(item.route)}
              >
                <div className="sc-slide-img">
                  <img src={item.img} alt={item.label} loading="lazy" />
                </div>
                <span className="sc-slide-name">{item.label}</span>
              </div>
            ))}
          </div>
          <div className="sc-fade-left" aria-hidden="true" />
          <div className="sc-fade-right" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ───────────────────────── */
export default function SolutionsCarousel() {
  return (
    <div className="sc-panel">
      <div className="sc-header">
        <div className="sc-header-title">
          <span className="sc-eyebrow">Our Solutions</span>
          <h2 className="sc-title">Integrated Tech Ecosystem</h2>
        </div>
      </div>

      <div className="sc-divider" />

      <div className="sc-rows">
        {CATEGORIES.map((cat) => (
          <CategoryRow key={cat.id} category={cat} />
        ))}
      </div>
    </div>
  );
}
